import { supabase, MedicalRecord, UserProfile } from './supabase'
import { medicalPQC, PQCEncryptedData } from './medicalPQCIntegration'
import { toast } from 'sonner'

// Tipos avançados para o sistema de registros médicos
export interface MedicalRecordCategory {
  id: string
  name: string
  description: string
  color: string
  icon: string
  required_fields: string[]
  template_fields: Record<string, any>
  validation_rules: Record<string, any>
}

export interface MedicalRecordTemplate {
  id: string
  category_id: string
  name: string
  description: string
  fields: Record<string, any>
  default_values: Record<string, any>
  is_active: boolean
}

export interface EnhancedMedicalRecord extends MedicalRecord {
  category?: MedicalRecordCategory
  template?: MedicalRecordTemplate
  version: number
  parent_record_id?: string
  previous_version_id?: string
  is_current_version?: boolean
  tags: string[]
  priority: 'low' | 'normal' | 'high' | 'critical'
  status: 'draft' | 'active' | 'archived' | 'deleted'
  follow_up_date?: string
  reminder_date?: string
  custom_fields: Record<string, any>
  // Campos específicos para allergy_intolerance
  clinical_status?: string
  verification_status?: string
  substance_display?: string
  allergy_type?: string
  allergy_categories?: string[]
  criticality?: string
  substance_code?: string
  onset?: string
  recorded_date?: string
  reactions?: any[]
  notes?: string[]
}

export interface RecordValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

export interface RecordSearchFilters {
  category?: string
  record_type?: string
  date_from?: string
  date_to?: string
  priority?: string
  status?: string
  tags?: string[]
  doctor_id?: string
  patient_id?: string
  include_historical?: boolean // Se true, inclui versões históricas; se false/undefined, apenas versões atuais
}

// Categorias predefinidas de registros médicos
export const MEDICAL_RECORD_CATEGORIES: MedicalRecordCategory[] = [
  {
    id: 'consultation',
    name: 'Consultas Médicas',
    description: 'Registros de consultas e atendimentos médicos',
    color: 'bg-blue-500',
    icon: 'Stethoscope',
    required_fields: ['title', 'description', 'patient_id', 'doctor_id'],
    template_fields: {
      chief_complaint: 'string',
      history_present_illness: 'text',
      physical_examination: 'text',
      assessment: 'text',
      plan: 'text'
    },
    validation_rules: {
      title: { minLength: 5, maxLength: 200 },
      description: { minLength: 10, maxLength: 2000 }
    }
  },
  {
    id: 'diagnosis',
    name: 'Diagnósticos',
    description: 'Diagnósticos médicos e condições identificadas',
    color: 'bg-red-500',
    icon: 'Brain',
    required_fields: ['title', 'diagnosis', 'patient_id', 'doctor_id'],
    template_fields: {
      primary_diagnosis: 'string',
      secondary_diagnoses: 'array',
      icd_codes: 'array',
      differential_diagnosis: 'text',
      prognosis: 'text'
    },
    validation_rules: {
      diagnosis: { minLength: 10, maxLength: 1000 }
    }
  },
  {
    id: 'prescription',
    name: 'Prescrições',
    description: 'Prescrições médicas e medicamentos',
    color: 'bg-green-500',
    icon: 'Pill',
    required_fields: ['title', 'medications', 'patient_id', 'doctor_id'],
    template_fields: {
      medications: 'array',
      dosage_instructions: 'text',
      duration: 'string',
      special_instructions: 'text',
      pharmacy_notes: 'text'
    },
    validation_rules: {
      medications: { minItems: 1 }
    }
  },
  {
    id: 'lab_result',
    name: 'Resultados Laboratoriais',
    description: 'Resultados de exames laboratoriais',
    color: 'bg-purple-500',
    icon: 'TestTube',
    required_fields: ['title', 'lab_results', 'patient_id', 'doctor_id'],
    template_fields: {
      test_name: 'string',
      test_date: 'date',
      results: 'object',
      reference_ranges: 'object',
      interpretation: 'text',
      lab_technician: 'string'
    },
    validation_rules: {
      lab_results: { required: true }
    }
  },
  {
    id: 'imaging',
    name: 'Exames de Imagem',
    description: 'Resultados de exames de imagem e radiologia',
    color: 'bg-indigo-500',
    icon: 'Microscope',
    required_fields: ['title', 'description', 'patient_id', 'doctor_id'],
    template_fields: {
      imaging_type: 'string',
      body_part: 'string',
      findings: 'text',
      impression: 'text',
      radiologist: 'string',
      image_urls: 'array'
    },
    validation_rules: {
      imaging_type: { required: true }
    }
  },
  {
    id: 'surgery',
    name: 'Procedimentos Cirúrgicos',
    description: 'Registros de cirurgias e procedimentos',
    color: 'bg-orange-500',
    icon: 'Activity',
    required_fields: ['title', 'description', 'patient_id', 'doctor_id'],
    template_fields: {
      procedure_name: 'string',
      surgeon: 'string',
      anesthesiologist: 'string',
      duration: 'string',
      complications: 'text',
      post_op_instructions: 'text'
    },
    validation_rules: {
      procedure_name: { required: true }
    }
  },
  {
    id: 'vaccination',
    name: 'Vacinações',
    description: 'Registros de vacinas e imunizações',
    color: 'bg-teal-500',
    icon: 'Shield',
    required_fields: ['title', 'description', 'patient_id', 'doctor_id'],
    template_fields: {
      vaccine_name: 'string',
      manufacturer: 'string',
      lot_number: 'string',
      dose_number: 'number',
      next_dose_date: 'date',
      adverse_reactions: 'text'
    },
    validation_rules: {
      vaccine_name: { required: true }
    }
  },
  {
    id: 'allergy_intolerance',
    name: 'Alergias e Intolerâncias',
    description: 'Registros de alergias e intolerâncias conforme HL7 FHIR R4',
    color: 'bg-yellow-500',
    icon: 'AlertTriangle',
    required_fields: ['title', 'patient_id', 'doctor_id'],
    template_fields: {
      clinical_status: 'select',
      verification_status: 'select',
      type: 'select',
      category: 'array',
      criticality: 'select',
      substance_code: 'string',
      substance_display: 'string',
      onset: 'date',
      recorded_date: 'date',
      reactions: 'array',
      notes: 'array'
    },
    validation_rules: {
      clinical_status: { required: true, enum: ['active', 'inactive', 'resolved'] },
      verification_status: { required: true, enum: ['unconfirmed', 'confirmed', 'refuted', 'entered-in-error'] },
      substance_display: { required: true, minLength: 2, maxLength: 200 }
    }
  }
]

// Templates predefinidos
export const MEDICAL_RECORD_TEMPLATES: MedicalRecordTemplate[] = [
  {
    id: 'routine-checkup',
    category_id: 'consultation',
    name: 'Consulta de Rotina',
    description: 'Template para consultas de rotina e check-ups',
    fields: {
      vital_signs: {
        blood_pressure: '',
        heart_rate: '',
        temperature: '',
        weight: '',
        height: ''
      },
      review_of_systems: '',
      physical_exam: '',
      assessment: '',
      plan: ''
    },
    default_values: {
      record_type: 'consultation',
      priority: 'normal'
    },
    is_active: true
  },
  {
    id: 'emergency-visit',
    category_id: 'consultation',
    name: 'Atendimento de Emergência',
    description: 'Template para atendimentos de emergência',
    fields: {
      chief_complaint: '',
      triage_level: '',
      vital_signs: {
        blood_pressure: '',
        heart_rate: '',
        temperature: '',
        oxygen_saturation: ''
      },
      emergency_assessment: '',
      immediate_interventions: '',
      disposition: ''
    },
    default_values: {
      record_type: 'consultation',
      priority: 'critical'
    },
    is_active: true
  },
  {
    id: 'prescription-template',
    category_id: 'prescription',
    name: 'Prescrição Padrão',
    description: 'Template para prescrições médicas',
    fields: {
      medications: [],
      allergies_checked: false,
      drug_interactions_checked: false,
      patient_counseled: false,
      follow_up_required: false
    },
    default_values: {
      record_type: 'prescription',
      priority: 'normal'
    },
    is_active: true
  },
  {
    id: 'allergy-intolerance-template',
    category_id: 'allergy_intolerance',
    name: 'Alergia/Intolerância FHIR R4',
    description: 'Template para registros de alergias e intolerâncias conforme HL7 FHIR R4',
    fields: {
      clinical_status: 'active',
      verification_status: 'confirmed',
      type: 'allergy',
      category: ['food'],
      criticality: 'low',
      substance_code: '',
      substance_display: '',
      onset: '',
      recorded_date: '',
      reactions: [{
        manifestation_codes: [],
        manifestation_displays: [],
        severity: 'mild',
        description: '',
        onset: ''
      }],
      notes: []
    },
    default_values: {
      record_type: 'allergy_intolerance',
      priority: 'normal',
      urgency: 'normal'
    },
    is_active: true
  }
]

// Sistema robusto de registros médicos
export class MedicalRecordsSystem {
  // Validar registro médico
  static validateRecord(record: Partial<EnhancedMedicalRecord>, category?: MedicalRecordCategory): RecordValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // Validações básicas
    if (!record.title || record.title.trim().length === 0) {
      errors.push('Título é obrigatório')
    }

    if (!record.patient_id) {
      errors.push('ID do paciente é obrigatório')
    }

    if (!record.doctor_id) {
      errors.push('ID do médico é obrigatório')
    }

    if (!record.record_type) {
      errors.push('Tipo de registro é obrigatório')
    }

    // Validações específicas da categoria
    if (category) {
      // Verificar campos obrigatórios
      for (const field of category.required_fields) {
        if (!record[field as keyof EnhancedMedicalRecord]) {
          errors.push(`Campo obrigatório ausente: ${field}`)
        }
      }

      // Aplicar regras de validação
      for (const [field, rules] of Object.entries(category.validation_rules)) {
        const value = record[field as keyof EnhancedMedicalRecord]
        
        if (rules.required && !value) {
          errors.push(`Campo ${field} é obrigatório`)
        }

        if (rules.minLength && typeof value === 'string' && value.length < rules.minLength) {
          errors.push(`Campo ${field} deve ter pelo menos ${rules.minLength} caracteres`)
        }

        if (rules.maxLength && typeof value === 'string' && value.length > rules.maxLength) {
          errors.push(`Campo ${field} deve ter no máximo ${rules.maxLength} caracteres`)
        }

        if (rules.minItems && Array.isArray(value) && value.length < rules.minItems) {
          errors.push(`Campo ${field} deve ter pelo menos ${rules.minItems} itens`)
        }
      }
    }

    // Avisos
    if (record.priority === 'critical' && !record.follow_up_date) {
      warnings.push('Registros críticos devem ter data de acompanhamento')
    }

    if (!record.description || record.description.trim().length < 10) {
      warnings.push('Descrição muito curta - considere adicionar mais detalhes')
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }

  // Criar registro com validação e criptografia PQC
  static async createRecord(record: Partial<EnhancedMedicalRecord>): Promise<{ data: any; error: any }> {
    try {
      // Encontrar categoria
      const category = MEDICAL_RECORD_CATEGORIES.find(c => c.id === record.record_type)
      
      // Validar registro
      const validation = this.validateRecord(record, category)
      
      if (!validation.isValid) {
        return {
          data: null,
          error: { message: `Erros de validação: ${validation.errors.join(', ')}` }
        }
      }

      // Mostrar avisos se houver
      if (validation.warnings.length > 0) {
        validation.warnings.forEach(warning => toast.warning(warning))
      }

      // Preparar dados sensíveis para criptografia
      const sensitiveData = {
        description: record.description,
        diagnosis: record.diagnosis,
        treatment: record.treatment,
        custom_fields: record.custom_fields || {}
      }

      // Criptografar dados sensíveis com PQC
      const encryptionResult = await medicalPQC.encryptMedicalRecord(
        sensitiveData,
        record.patient_id!,
        record.doctor_id!
      )

      if (!encryptionResult) {
        return {
          data: null,
          error: { message: 'Falha na criptografia dos dados médicos' }
        }
      }

      // Preparar dados do registro (sem dados sensíveis em texto claro)
      const recordData = {
        ...record,
        description: '', // Limpar dados sensíveis
        diagnosis: '',
        treatment: '',
        is_encrypted: true,
        encryption_key_id: encryptionResult.keyId
      }

      // Remover apenas campos que não existem na tabela medical_records
      delete recordData.version
      // Manter tags, priority, status e custom_fields - campos foram adicionados à tabela

      // Criar registro no banco
      const { data, error } = await supabase
        .from('medical_records')
        .insert(recordData)
        .select('*')
        .single()

      if (error) {
        return { data: null, error }
      }

      // Salvar dados criptografados separadamente
      const { error: encryptedError } = await supabase
        .from('encrypted_data')
        .insert({
          data_type: 'medical_record',
          encrypted_content: encryptionResult.encrypted.encrypted_content,
          encryption_algorithm: 'pqc_kyber',
          key_id: encryptionResult.keyId,
          iv: 'simulated_iv',
          auth_tag: 'simulated_auth_tag',
          metadata: {
            ...encryptionResult.encrypted.encryption_metadata,
            record_id: data.id,
            patient_id: record.patient_id
          }
        })

      if (encryptedError) {
        // Rollback: remover registro se falhar ao salvar dados criptografados
        await supabase.from('medical_records').delete().eq('id', data.id)
        return { data: null, error: encryptedError }
      }

      // Log de auditoria
      await this.createAuditLog({
        entity_type: 'medical_record',
        entity_id: data.id,
        action_type: 'create',
        user_id: record.doctor_id!,
        details: { 
          category: category?.name, 
          priority: record.priority,
          encrypted: true,
          encryption_key_id: encryptionResult.keyId
        }
      })

      // Retornar dados descriptografados para exibição
      const decryptedData = {
        ...data,
        ...sensitiveData
      }

      return { data: decryptedData, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Buscar registros com filtros avançados e descriptografia PQC
  static async searchRecords(filters: RecordSearchFilters, userId?: string): Promise<{ data: any[]; error: any }> {
    try {
      let query = supabase
        .from('medical_records')
        .select('*')

      // Por padrão, buscar apenas versões atuais (imutabilidade)
      if (filters.include_historical !== true) {
        query = query.eq('is_current_version', true)
      }

      // Aplicar filtros
      if (filters.patient_id) {
        query = query.eq('patient_id', filters.patient_id)
      }

      if (filters.doctor_id) {
        query = query.eq('doctor_id', filters.doctor_id)
      }

      if (filters.record_type) {
        query = query.eq('record_type', filters.record_type)
      }

      if (filters.priority) {
        query = query.eq('priority', filters.priority)
      }

      if (filters.status) {
        query = query.eq('status', filters.status)
      }

      if (filters.date_from) {
        query = query.gte('created_at', filters.date_from)
      }

      if (filters.date_to) {
        query = query.lte('created_at', filters.date_to)
      }

      // Ordenar por data de criação (mais recentes primeiro)
      query = query.order('created_at', { ascending: false })

      const { data, error } = await query

      if (error) {
        return { data: [], error }
      }

      // Filtrar por tags se especificado
      let filteredData = data || []
      if (filters.tags && filters.tags.length > 0) {
        filteredData = filteredData.filter(record => 
          filters.tags!.some(tag => record.tags?.includes(tag))
        )
      }

      // Descriptografar dados sensíveis para registros criptografados
      if (userId) {
        console.log('🔐 [DEBUG] Iniciando descriptografia para userId:', userId)
        console.log('📊 [DEBUG] Registros para descriptografar:', filteredData.map(r => ({
          id: r.id,
          is_encrypted: r.is_encrypted,
          encryption_key_id: r.encryption_key_id
        })))
        
        const decryptedData = await Promise.all(
          filteredData.map(async (record) => {
            console.log(`🔍 [DEBUG] Processando registro ${record.id}:`, {
              is_encrypted: record.is_encrypted,
              encryption_key_id: record.encryption_key_id
            })
            
            if (record.is_encrypted && record.encryption_key_id) {
              try {
                console.log(`🔑 [DEBUG] Buscando dados criptografados para registro ${record.id}`)
                
                // Buscar dados criptografados
                const { data: encryptedData, error: encryptedError } = await supabase
                  .from('encrypted_data')
                  .select('*')
                  .eq('key_id', record.encryption_key_id)
                  .eq('data_type', 'medical_record')
                  .single()

                console.log(`📦 [DEBUG] Dados criptografados encontrados para ${record.id}:`, {
                  found: !!encryptedData,
                  error: encryptedError,
                  hasContent: !!encryptedData?.encrypted_content
                })

                if (encryptedData) {
                  // Preparar dados para descriptografia
                  const pqcData: PQCEncryptedData = {
                    encrypted_content: encryptedData.encrypted_content,
                    kyber_public_key: '', // Será obtido da chave
                    dilithium_signature: '', // Será obtido da chave
                    encryption_metadata: encryptedData.metadata || {}
                  }

                  console.log(`🔓 [DEBUG] Iniciando descriptografia para registro ${record.id}`)
                  
                  // Descriptografar dados
                  const decryptedContent = await medicalPQC.decryptMedicalRecord(
                    pqcData,
                    userId,
                    record.patient_id,
                    record.id
                  )

                  console.log(`✅ [DEBUG] Resultado da descriptografia para ${record.id}:`, {
                    success: !!decryptedContent,
                    decryptedFields: decryptedContent ? Object.keys(decryptedContent) : [],
                    hasDescription: !!decryptedContent?.description,
                    hasDiagnosis: !!decryptedContent?.diagnosis,
                    hasTreatment: !!decryptedContent?.treatment,
                    hasCustomFields: !!decryptedContent?.custom_fields
                  })

                  if (decryptedContent) {
                    const mergedRecord = {
                      ...record,
                      ...decryptedContent
                    }
                    
                    console.log(`🔄 [DEBUG] Registro mesclado ${record.id}:`, {
                      title: mergedRecord.title,
                      hasDescription: !!mergedRecord.description,
                      hasDiagnosis: !!mergedRecord.diagnosis,
                      hasTreatment: !!mergedRecord.treatment,
                      tagsCount: mergedRecord.tags?.length || 0,
                      priority: mergedRecord.priority,
                      status: mergedRecord.status
                    })
                    
                    return mergedRecord
                  }
                }
              } catch (error) {
                console.error(`❌ [ERROR] Erro ao descriptografar registro ${record.id}:`, error)
                // Retornar registro sem dados sensíveis em caso de erro
              }
            }
            
            console.log(`📝 [DEBUG] Retornando registro não criptografado ${record.id}:`, {
              title: record.title,
              hasDescription: !!record.description,
              hasDiagnosis: !!record.diagnosis,
              hasTreatment: !!record.treatment,
              tagsCount: record.tags?.length || 0,
              priority: record.priority,
              status: record.status
            })
            
            return record
          })
        )

        return { data: decryptedData, error: null }
      }

      return { data: filteredData, error: null }
    } catch (error) {
      return { data: [], error }
    }
  }

  // Atualizar registro (criar nova versão imutável)
  static async updateRecord(recordId: string, updates: Partial<EnhancedMedicalRecord>): Promise<{ data: any; error: any }> {
    try {
      // Buscar registro atual
      const { data: currentRecord, error: fetchError } = await supabase
        .from('medical_records')
        .select('*')
        .eq('id', recordId)
        .single()

      if (fetchError || !currentRecord) {
        return { data: null, error: fetchError || { message: 'Registro não encontrado' } }
      }

      // Validar atualizações
      const category = MEDICAL_RECORD_CATEGORIES.find(c => c.id === currentRecord.record_type)
      const validation = this.validateRecord({ ...currentRecord, ...updates }, category)
      
      if (!validation.isValid) {
        return {
          data: null,
          error: { message: `Erros de validação: ${validation.errors.join(', ')}` }
        }
      }

      // Incrementar versão
      const newVersion = (currentRecord.version || 1) + 1

      // PASSO 1: Marcar registro atual como não sendo mais a versão atual
      const { error: updateCurrentError } = await supabase
        .from('medical_records')
        .update({ is_current_version: false })
        .eq('id', recordId)

      if (updateCurrentError) {
        return { data: null, error: updateCurrentError }
      }

      // PASSO 2: Preparar dados para nova versão
      let newRecordData = {
        ...currentRecord,
        ...updates,
        version: newVersion,
        previous_version_id: recordId, // Referência ao registro anterior
        is_current_version: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      // Remover campos que não devem ser copiados
      delete newRecordData.id // Novo ID será gerado automaticamente

      // Preparar dados de criptografia se necessário
      let encryptionResult = null
      if (updates.description || updates.diagnosis || updates.treatment || updates.custom_fields) {
        const sensitiveData = {
          description: updates.description || currentRecord.description,
          diagnosis: updates.diagnosis || currentRecord.diagnosis,
          treatment: updates.treatment || currentRecord.treatment,
          custom_fields: updates.custom_fields || currentRecord.custom_fields
        }

        // Criptografar dados sensíveis com PQC
        encryptionResult = await medicalPQC.encryptMedicalRecord(
          sensitiveData,
          currentRecord.patient_id,
          updates.doctor_id || currentRecord.doctor_id
        )

        if (encryptionResult) {
          // Limpar dados sensíveis do novo registro
          newRecordData = {
            ...newRecordData,
            description: '',
            diagnosis: '',
            treatment: '',
            encryption_key_id: encryptionResult.keyId
          }
        }
      }

      // PASSO 3: Criar novo registro com nova versão
      const { data: newRecord, error: insertError } = await supabase
        .from('medical_records')
        .insert(newRecordData)
        .select('*')
        .single()

      if (insertError) {
        // Reverter alteração no registro atual em caso de erro
        await supabase
          .from('medical_records')
          .update({ is_current_version: true })
          .eq('id', recordId)
        
        return { data: null, error: insertError }
      }

      // PASSO 4: Criar entrada de dados criptografados se necessário
      if (encryptionResult && newRecord) {
        const { error: encryptedDataError } = await supabase
          .from('encrypted_data')
          .insert({
            data_type: 'medical_record',
            encrypted_content: encryptionResult.encrypted.encrypted_content,
            encryption_algorithm: 'pqc_kyber',
            key_id: encryptionResult.keyId,
            iv: 'simulated_iv',
            auth_tag: 'simulated_auth_tag',
            metadata: {
              ...encryptionResult.encrypted.encryption_metadata,
              record_id: newRecord.id, // Armazenar record_id no metadata
              patient_id: currentRecord.patient_id
            }
          })

        if (encryptedDataError) {
          console.error('Erro ao criar entrada encrypted_data:', encryptedDataError)
          // Não falhar a operação por causa disso, mas logar o erro
        }
      }

      // Log de auditoria
      await this.createAuditLog({
        entity_type: 'medical_record',
        entity_id: newRecord.id,
        action_type: 'update',
        user_id: updates.doctor_id || currentRecord.doctor_id,
        details: { 
          version: newVersion, 
          changes: Object.keys(updates),
          previous_version_id: recordId,
          immutable_update: true
        }
      })

      return { data: newRecord, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Arquivar registro
  static async archiveRecord(recordId: string, userId: string): Promise<{ data: any; error: any }> {
    return this.updateRecord(recordId, { status: 'archived' })
  }

  // Obter estatísticas de registros
  static async getRecordStatistics(userId: string, role: 'patient' | 'doctor'): Promise<{ data: any; error: any }> {
    try {
      const field = role === 'patient' ? 'patient_id' : 'doctor_id'
      
      const { data, error } = await supabase
        .from('medical_records')
        .select('record_type, priority, status, created_at, is_current_version')
        .eq(field, userId)
        .eq('status', 'active')
        .or('is_current_version.is.null,is_current_version.eq.true')

      if (error) {
        return { data: null, error }
      }

      // Calcular estatísticas
      const stats = {
        total: data.length,
        byType: {} as Record<string, number>,
        byPriority: {} as Record<string, number>,
        recent: data.filter(r => {
          const recordDate = new Date(r.created_at)
          const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
          return recordDate >= weekAgo
        }).length
      }

      // Contar por tipo
      data.forEach(record => {
        stats.byType[record.record_type] = (stats.byType[record.record_type] || 0) + 1
        stats.byPriority[record.priority] = (stats.byPriority[record.priority] || 0) + 1
      })

      return { data: stats, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Criar log de auditoria
  private static async createAuditLog(audit: {
    entity_type: string
    entity_id: string
    action_type: string
    user_id: string
    details?: any
  }) {
    try {
      await supabase
        .from('blockchain_audit')
        .insert({
          ...audit,
          blockchain_network: 'ethereum',
          transaction_hash: `0x${Math.random().toString(16).substr(2, 64)}`, // Mock hash
          event_data: audit.details
        })
    } catch (error) {
      console.error('Erro ao criar log de auditoria:', error)
    }
  }

  // Obter template por ID
  static getTemplate(templateId: string): MedicalRecordTemplate | undefined {
    return MEDICAL_RECORD_TEMPLATES.find(t => t.id === templateId)
  }

  // Obter categoria por ID
  static getCategory(categoryId: string): MedicalRecordCategory | undefined {
    return MEDICAL_RECORD_CATEGORIES.find(c => c.id === categoryId)
  }

  // Aplicar template a um registro
  static applyTemplate(templateId: string, baseRecord: Partial<EnhancedMedicalRecord>): Partial<EnhancedMedicalRecord> {
    const template = this.getTemplate(templateId)
    if (!template) {
      return baseRecord
    }

    return {
      ...baseRecord,
      ...template.default_values,
      custom_fields: {
        ...baseRecord.custom_fields,
        ...template.fields
      }
    }
  }

  // Obter navegação entre versões (anterior/próxima) - GENÉRICO
  static async getVersionNavigation(recordId: string, requesterId: string): Promise<{ data: { previous: string | null; next: string | null; current: number; total: number } | null; error: any }> {
    try {
      console.log('🧭 [DEBUG] Iniciando navegação de versões')
      console.log('📄 [DEBUG] Record ID:', recordId)
      console.log('👤 [DEBUG] Requester ID:', requesterId)
      
      // Buscar registro atual
      const { data: currentRecord, error: currentError } = await supabase
        .from('medical_records')
        .select('id, version, previous_version_id, is_current_version, record_type, patient_id, doctor_id')
        .eq('id', recordId)
        .single()

      if (currentError || !currentRecord) {
        console.error('❌ [DEBUG] Erro ao buscar registro atual:', currentError)
        return { data: null, error: currentError || { message: 'Registro não encontrado' } }
      }

      console.log('📋 [DEBUG] Registro atual encontrado:', {
        id: currentRecord.id,
        version: currentRecord.version,
        previous_version_id: currentRecord.previous_version_id,
        is_current_version: currentRecord.is_current_version,
        record_type: currentRecord.record_type
      })

      // Buscar todas as versões da cadeia
      console.log('🔍 [DEBUG] Buscando todas as versões do tipo:', currentRecord.record_type)
      const { data: allVersions, error: versionsError } = await supabase
        .from('medical_records')
        .select('id, version, previous_version_id, is_current_version, patient_id, doctor_id')
        .eq('record_type', currentRecord.record_type)
        .order('version', { ascending: true })

      if (versionsError) {
        console.error('❌ [DEBUG] Erro ao buscar versões:', versionsError)
        return { data: null, error: versionsError }
      }

      console.log('📊 [DEBUG] Total de versões encontradas:', allVersions?.length || 0)

      // Filtrar versões da mesma cadeia
      const chainVersions = allVersions.filter(v => {
        // Incluir se é o registro atual
        if (v.id === recordId) return true
        // Incluir se aponta para o registro atual como versão anterior
        if (v.previous_version_id === recordId) return true
        // Incluir se o registro atual aponta para ele como versão anterior
        if (currentRecord.previous_version_id === v.id) return true
        return false
      }).sort((a, b) => a.version - b.version)

      console.log('🔗 [DEBUG] Versões da cadeia filtradas:', chainVersions.map(v => ({ id: v.id, version: v.version, previous_version_id: v.previous_version_id })))

      const currentIndex = chainVersions.findIndex(v => v.id === recordId)
      const previousVersion = currentIndex > 0 ? chainVersions[currentIndex - 1] : null
      const nextVersion = currentIndex < chainVersions.length - 1 ? chainVersions[currentIndex + 1] : null

      console.log('📍 [DEBUG] Posição atual na cadeia:', currentIndex)
      console.log('⬅️ [DEBUG] Versão anterior:', previousVersion?.id || 'nenhuma')
      console.log('➡️ [DEBUG] Próxima versão:', nextVersion?.id || 'nenhuma')

      // Buscar versão anterior se existir (incluir histórico)
      if (previousVersion) {
        console.log('🔍 [DEBUG] Verificando acesso à versão anterior:', previousVersion.id)
        const { data: previousRecord, error: previousError } = await this.getRecordById(previousVersion.id, requesterId, true)
        if (!previousRecord) {
          console.log('❌ [DEBUG] Não foi possível acessar versão anterior:', previousError)
          // Se não conseguir acessar a versão anterior, remover da navegação
          previousVersion.id = null
        } else {
          console.log('✅ [DEBUG] Acesso à versão anterior confirmado')
        }
      }

      // Buscar próxima versão se existir (incluir histórico)
      if (nextVersion) {
        console.log('🔍 [DEBUG] Verificando acesso à próxima versão:', nextVersion.id)
        const { data: nextRecord, error: nextError } = await this.getRecordById(nextVersion.id, requesterId, true)
        if (!nextRecord) {
          console.log('❌ [DEBUG] Não foi possível acessar próxima versão:', nextError)
          // Se não conseguir acessar a próxima versão, remover da navegação
          nextVersion.id = null
        } else {
          console.log('✅ [DEBUG] Acesso à próxima versão confirmado')
        }
      }

      const navigationResult = {
        previous: previousVersion?.id || null,
        next: nextVersion?.id || null,
        current: currentRecord.version,
        total: chainVersions.length
      }

      console.log('🎯 [DEBUG] Resultado da navegação:', navigationResult)

      return {
        data: navigationResult,
        error: null
      }
    } catch (error) {
      console.error('Erro ao buscar navegação de versões:', error)
      return { data: null, error: { message: 'Erro interno do servidor' } }
    }
  }

  // Buscar registro por ID - GENÉRICO
  static async getRecordById(recordId: string, requesterId: string, includeHistorical: boolean = false): Promise<{ data: EnhancedMedicalRecord | null; error: any }> {
    try {
      console.log('🔍 [DEBUG] Buscando registro por ID:', recordId, 'Requester:', requesterId, 'Include Historical:', includeHistorical)
      
      // Buscar registro no banco
      const query = supabase
        .from('medical_records')
        .select('*')
        .eq('id', recordId)

      // Nota: Removido o filtro is_current_version para permitir busca de versões históricas
      // A verificação de permissão será feita após encontrar o registro

      const { data: record, error: fetchError } = await query.single()

      if (fetchError) {
        console.error('❌ [DEBUG] Erro ao buscar registro:', fetchError)
        return { data: null, error: fetchError }
      }

      if (!record) {
        console.error('❌ [DEBUG] Registro não encontrado')
        return { data: null, error: { message: 'Registro não encontrado' } }
      }

      console.log('📋 [DEBUG] Registro encontrado:', record)

      // Verificar permissões
      if (record.doctor_id !== requesterId && record.patient_id !== requesterId) {
        return { data: null, error: { message: 'Acesso negado' } }
      }

      // Se o registro está criptografado, descriptografar
      if (record.is_encrypted && record.encryption_key_id) {
        try {
          console.log('🔍 [DEBUG] Buscando dados criptografados para record_id:', record.id)
          
          // Primeiro tentar buscar por record_id no metadata (novo sistema)
          let { data: encryptedData, error: encryptedError } = await supabase
            .from('encrypted_data')
            .select('*')
            .eq('metadata->>record_id', record.id)
            .eq('data_type', 'medical_record')
            .single()

          // Se não encontrar por record_id, tentar por key_id (sistema antigo)
          if (encryptedError || !encryptedData) {
            console.log('🔍 [DEBUG] Não encontrado por record_id, tentando por key_id:', record.encryption_key_id)
            const fallbackResult = await supabase
              .from('encrypted_data')
              .select('*')
              .eq('key_id', record.encryption_key_id)
              .eq('data_type', 'medical_record')
              .single()
            
            encryptedData = fallbackResult.data
          }

          console.log('📋 [DEBUG] Dados criptografados encontrados:', !!encryptedData)

          if (encryptedData) {
            const decryptedData = await medicalPQC.decryptMedicalRecord(
              encryptedData,
              requesterId,
              record.patient_id,
              record.id
            )

            if (decryptedData) {
              const mergedRecord = {
                ...record,
                ...decryptedData,
                is_encrypted: true
              }
              return { data: mergedRecord, error: null }
            }
          }
        } catch (error) {
          console.error('❌ [ERROR] Erro ao descriptografar registro:', error)
        }
      }

      // Retornar registro sem criptografia ou em caso de erro na descriptografia
      return { data: record, error: null }

    } catch (error) {
      console.error('Erro ao buscar registro por ID:', error)
      return { data: null, error: { message: 'Erro interno do servidor' } }
    }
  }
}

// Exportar sistema como padrão
export default MedicalRecordsSystem